import React, { useState, useEffect } from 'react';
import { useAuth } from '../components/AuthProvider';
import { supabase } from '../utils/supabase';
import { Order } from '../types';
import { useNavigate } from 'react-router-dom';
import { useOrderNotifications } from '../hooks/useOrderNotifications';
import { useLanguage } from '../contexts/LanguageContext';
import { getTranslation, getOrderStatusTranslation } from '../utils/translations';
import LanguageSwitcher from '../components/LanguageSwitcher';

const AdminDashboard: React.FC = () => {
  const [recentOrders, setRecentOrders] = useState<Order[]>([]);
  const [stats, setStats] = useState({ totalUsers: 0, totalProducts: 0, totalOrders: 0, pendingOrders: 0 });
  const { user, signOut } = useAuth();
  const navigate = useNavigate();
  const { language } = useLanguage();
  const t = (key: string) => getTranslation(key as any, language);
  useOrderNotifications();

  useEffect(() => {
    fetchRecentOrders();
    fetchStats();
    
    const subscription = supabase
      .channel('admin_dashboard_realtime')
      .on('postgres_changes', { event: '*', schema: 'public', table: 'habib_orders' }, (payload) => {
        console.log('Dashboard - Orders change:', payload);
        fetchRecentOrders();
        fetchStats();
      })
      .on('postgres_changes', { event: '*', schema: 'public', table: 'habib_order_items' }, (payload) => {
        console.log('Dashboard - Order items change:', payload);
        fetchRecentOrders();
        fetchStats();
      })
      .subscribe((status) => {
        console.log('Dashboard subscription status:', status);
      });

    return () => {
      subscription.unsubscribe();
    };
  }, []);

  const fetchRecentOrders = async () => {
    const { data } = await supabase
      .from('habib_orders')
      .select('*')
      .order('created_at', { ascending: false })
      .limit(7);
    if (data) setRecentOrders(data);
  };

  const fetchStats = async () => {
    const [usersResult, productsResult, ordersResult, pendingResult] = await Promise.all([
      supabase.from('habib_users').select('id', { count: 'exact' }),
      supabase.from('habib_products').select('id', { count: 'exact' }),
      supabase.from('habib_orders').select('id', { count: 'exact' }),
      supabase.from('habib_orders').select('id', { count: 'exact' }).eq('status', 'Permintaan Pesanan')
    ]);
    
    setStats({
      totalUsers: usersResult.count || 0,
      totalProducts: productsResult.count || 0,
      totalOrders: ordersResult.count || 0,
      pendingOrders: pendingResult.count || 0
    });
  };

  return (
    <div className="min-h-screen" style={{background: 'linear-gradient(135deg, #f0fdf4 0%, #dcfce7 50%, #bbf7d0 100%)'}}>
      <nav className="bg-white/90 backdrop-blur-md shadow-soft" style={{borderBottom: '1px solid #05472A20'}}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center py-4 sm:py-0 sm:h-20 gap-4 sm:gap-0">
            <div className="flex items-center">
              <div className="w-10 h-10 rounded-xl mr-3 flex items-center justify-center shadow-lg overflow-hidden" style={{backgroundColor: '#05472A'}}>
                <img 
                  src="https://edu.almuwasholah.com/wp-content/uploads/2026/02/logo-habib-1.png"
                  alt="Logo"
                  className="w-full h-full object-contain"
                />
              </div>
              <h1 className="text-xl sm:text-2xl font-bold" style={{color: '#05472A'}}>{t('adminDashboard')}</h1>
            </div>
            <div className="flex items-center gap-2 sm:gap-4">
              <LanguageSwitcher />
              <span className="hidden lg:block text-sm" style={{color: '#05472A'}}>{t('hi')}, {user?.username}</span>
              <button onClick={signOut} className="bg-white hover:bg-green-50 border px-4 py-2 sm:px-6 sm:py-3 rounded-xl font-semibold transition-all duration-300 shadow-soft hover:shadow-medium" style={{color: '#05472A', borderColor: '#05472A40'}}>
                <span className="hidden sm:inline">{t('signOut')}</span>
                <svg className="w-4 h-4 sm:hidden" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
                </svg>
              </button>
            </div>
          </div>
        </div>
      </nav>

      <main className="max-w-7xl mx-auto py-6 sm:py-8 px-4 sm:px-6 lg:px-8">
        {/* Stats Row: Users & Orders */}
        <div className="grid grid-cols-2 gap-4 sm:gap-6 mb-6 sm:mb-8">
          <div 
            className="bg-white rounded-2xl shadow-soft hover:shadow-medium transition-all duration-300 border p-4 sm:p-6 cursor-pointer hover:scale-105" 
            style={{borderColor: '#05472A20', backgroundColor: '#05472A05'}}
            onClick={() => navigate('/admin/users')}
          >
            <div className="flex items-center justify-between mb-3">
              <div className="w-10 h-10 rounded-xl flex items-center justify-center" style={{backgroundColor: '#05472A'}}>
                <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197m13.5-9a2.5 2.5 0 11-5 0 2.5 2.5 0 015 0z" />
                </svg>
              </div>
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" style={{color: '#05472A99'}}>
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
              </svg>
            </div>
            <h3 className="text-xs sm:text-sm font-semibold mb-1" style={{color: '#05472A99'}}>{t('totalUsers')}</h3>
            <p className="text-2xl sm:text-3xl font-bold" style={{color: '#05472A'}}>{stats.totalUsers}</p>
          </div>
          
          <div 
            className="bg-white rounded-2xl shadow-soft hover:shadow-medium transition-all duration-300 border p-4 sm:p-6 cursor-pointer hover:scale-105"
            style={{borderColor: '#05472A20', backgroundColor: '#05472A05'}}
            onClick={() => navigate('/admin/orders')}
          >
            <div className="flex items-center justify-between mb-3">
              <div className="w-10 h-10 rounded-xl flex items-center justify-center" style={{backgroundColor: '#05472A'}}>
                <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v10a2 2 0 002 2h8a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
                </svg>
              </div>
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" style={{color: '#05472A99'}}>
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
              </svg>
            </div>
            <h3 className="text-xs sm:text-sm font-semibold mb-1" style={{color: '#05472A99'}}>{t('totalOrders')}</h3>
            <p className="text-2xl sm:text-3xl font-bold" style={{color: '#05472A'}}>{stats.totalOrders}</p>
          </div>
        </div>

        {/* Products & Product Categories - grouped together */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 sm:gap-6 mb-6 sm:mb-8">
          <div 
            className="bg-white rounded-2xl shadow-soft hover:shadow-medium transition-all duration-300 border p-5 sm:p-6 cursor-pointer hover:scale-105" 
            style={{borderColor: '#05472A20', backgroundColor: '#05472A05'}}
            onClick={() => navigate('/admin/products')}
          >
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 sm:w-14 sm:h-14 rounded-xl flex items-center justify-center flex-shrink-0" style={{backgroundColor: '#05472A'}}>
                <svg className="w-6 h-6 sm:w-7 sm:h-7 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4" />
                </svg>
              </div>
              <div className="flex-1 min-w-0">
                <h3 className="text-base sm:text-lg font-bold mb-0.5" style={{color: '#05472A'}}>{t('guestServingProduct')}</h3>
                <p className="text-xs sm:text-sm" style={{color: '#05472A80'}}>{stats.totalProducts} {t('guestServingProduct').toLowerCase()}</p>
              </div>
              <svg className="w-5 h-5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24" style={{color: '#05472A99'}}>
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
              </svg>
            </div>
          </div>

          <div 
            className="bg-white rounded-2xl shadow-soft hover:shadow-medium transition-all duration-300 border p-5 sm:p-6 cursor-pointer hover:scale-105" 
            style={{borderColor: '#05472A20', backgroundColor: '#05472A05'}}
            onClick={() => navigate('/admin/categories')}
          >
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 sm:w-14 sm:h-14 rounded-xl flex items-center justify-center flex-shrink-0" style={{backgroundColor: '#05472A'}}>
                <svg className="w-6 h-6 sm:w-7 sm:h-7 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 7h.01M7 3h5c.512 0 1.024.195 1.414.586l7 7a2 2 0 010 2.828l-7 7a2 2 0 01-2.828 0l-7-7A1.994 1.994 0 013 12V7a4 4 0 014-4z" />
                </svg>
              </div>
              <div className="flex-1 min-w-0">
                <h3 className="text-base sm:text-lg font-bold mb-0.5" style={{color: '#05472A'}}>{t('Product Categories')}</h3>
                <p className="text-xs sm:text-sm" style={{color: '#05472A80'}}>{t('Manage Product Categories')}</p>
              </div>
              <svg className="w-5 h-5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24" style={{color: '#05472A99'}}>
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
              </svg>
            </div>
          </div>
        </div>

        {/* Menu Food & Menu Categories - grouped together */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 sm:gap-6 mb-6 sm:mb-8">
          <div 
            className="bg-white rounded-2xl shadow-soft hover:shadow-medium transition-all duration-300 border p-5 sm:p-6 cursor-pointer hover:scale-105" 
            style={{borderColor: '#05472A20', backgroundColor: '#05472A05'}}
            onClick={() => navigate('/admin/menu')}
          >
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 sm:w-14 sm:h-14 rounded-xl flex items-center justify-center flex-shrink-0" style={{backgroundColor: '#05472A'}}>
                <svg className="w-6 h-6 sm:w-7 sm:h-7 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
                </svg>
              </div>
              <div className="flex-1 min-w-0">
                <h3 className="text-base sm:text-lg font-bold mb-0.5" style={{color: '#05472A'}}>{t('menuFoodDrink')}</h3>
                <p className="text-xs sm:text-sm" style={{color: '#05472A80'}}>{t('manageRestaurantMenu')}</p>
              </div>
              <svg className="w-5 h-5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24" style={{color: '#05472A99'}}>
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
              </svg>
            </div>
          </div>

          <div 
            className="bg-white rounded-2xl shadow-soft hover:shadow-medium transition-all duration-300 border p-5 sm:p-6 cursor-pointer hover:scale-105" 
            style={{borderColor: '#05472A20', backgroundColor: '#05472A05'}}
            onClick={() => navigate('/admin/menu-categories')}
          >
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 sm:w-14 sm:h-14 rounded-xl flex items-center justify-center flex-shrink-0" style={{backgroundColor: '#05472A'}}>
                <svg className="w-6 h-6 sm:w-7 sm:h-7 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 7h.01M7 3h5c.512 0 1.024.195 1.414.586l7 7a2 2 0 010 2.828l-7 7a2 2 0 01-2.828 0l-7-7A1.994 1.994 0 013 12V7a4 4 0 014-4z" />
                </svg>
              </div>
              <div className="flex-1 min-w-0">
                <h3 className="text-base sm:text-lg font-bold mb-0.5" style={{color: '#05472A'}}>{t('menuCategories')}</h3>
                <p className="text-xs sm:text-sm" style={{color: '#05472A80'}}>{t('manageMenuCategories')}</p>
              </div>
              <svg className="w-5 h-5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24" style={{color: '#05472A99'}}>
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
              </svg>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-2xl shadow-soft hover:shadow-medium transition-all duration-300 border p-6 sm:p-8" style={{borderColor: '#05472A20'}}>
          <div className="flex items-center justify-between mb-6">
            <div>
              <h2 className="text-xl sm:text-2xl font-bold mb-2" style={{color: '#05472A'}}>{t('recentOrders')}</h2>
              <p className="text-sm" style={{color: '#05472A99'}}>{t('realtimeOrderUpdates')}</p>
            </div>
            <div className="w-3 h-3 rounded-full animate-pulse" style={{backgroundColor: '#05472A'}}></div>
          </div>
          
          {recentOrders.length === 0 ? (
            <div className="text-center py-8">
              <div className="w-16 h-16 rounded-full mx-auto mb-4 flex items-center justify-center" style={{backgroundColor: '#05472A10'}}>
                <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24" style={{color: '#05472A99'}}>
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v10a2 2 0 002 2h8a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
                </svg>
              </div>
              <p style={{color: '#05472A99'}}>{t('noOrdersYet')}</p>
            </div>
          ) : (
            <div className="space-y-3 sm:space-y-4">
              {recentOrders.map((order) => (
                <div 
                  key={order.id} 
                  className="flex flex-col sm:flex-row justify-between items-start sm:items-center p-4 sm:p-5 rounded-xl gap-3 sm:gap-0 cursor-pointer hover:shadow-soft transition-all duration-300 hover:scale-105" 
                  style={{background: 'linear-gradient(135deg, #f0fdf4 0%, #dcfce7 50%)'}}
                  onClick={() => navigate(`/admin/order/${order.id}`)}
                >
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-1">
                      <p className="font-bold text-sm sm:text-base" style={{color: '#05472A'}}>{t('order')} #{order.id}</p>
                      <div className="w-2 h-2 rounded-full" style={{backgroundColor: '#05472A'}}></div>
                    </div>
                    <p className="text-xs sm:text-sm mb-1" style={{color: '#05472A99'}}>{t('items')}: {order.total_items}</p>
                    <p className="text-xs" style={{color: '#05472A80'}}>
                      {new Date(order.created_at).toLocaleString()}
                    </p>
                    {order.note && (
                      <div className="mt-2 p-2 bg-blue-50 rounded-lg border border-blue-200">
                        <p className="text-xs font-semibold text-blue-700">Note: {order.note}</p>
                      </div>
                    )}
                  </div>
                  <div className="flex items-center gap-2">
                    <span className={`px-3 py-1 rounded-full text-xs font-semibold ${
                      order.status === 'Pesanan Selesai' 
                        ? 'bg-green-100 border border-green-200' 
                        : 'bg-yellow-100 border border-yellow-200'
                    }`} style={order.status === 'Pesanan Selesai' ? {color: '#05472A'} : {color: '#b45309'}}>
                      {getOrderStatusTranslation(order.status, language)}
                    </span>
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" style={{color: '#05472A99'}}>
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                    </svg>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </main>
    </div>
  );
};

export default AdminDashboard;
